import React, { useState } from 'react';
import { AdminWindowSwitcher } from '../../components/AdminShell';
import useReport from './useReport';

const WINDOWS = [
  { value: 7, label: '7d' }, { value: 30, label: '30d' }, { value: 60, label: '60d' },
  { value: 90, label: '90d' }, { value: 365, label: '1y' },
];

/** Whole-percent share; null when there is no denominator (which is not 0%). */
const share = (n, d) => (d ? Math.round((n / d) * 100) : null);
const pctText = (p) => (p === null ? '—' : `${p}%`);

const fmtDay = (iso) => (iso
  ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');
const fmtWhen = (iso) => (iso
  ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '—');

// Plain-English labels for the runner's internal keys (design handoff 2026-10-29).
const ASK_LABELS = {
  basics: 'Finish profile',
  photo: 'Add a photo',
  video: 'Add a video',
  academics: 'Add academics',
  measurements: 'Add measurements',
  card: 'Make trading card',
  acsi: 'Take the ACSI-28',
};
const askLabel = (a) => (a ? ASK_LABELS[a] || a : '—');

const stepLabel = (key) => {
  const m = /^(email|push)_d(\d+)$/.exec(key || '');
  if (!m) return key;
  if (m[1] === 'email') return m[2] === '0' ? 'Welcome email · day 0' : `Email · day ${m[2]}`;
  return `Push · day ${m[2]}`;
};

const STATUS_CHIPS = {
  sent: ['Sent', 'green'],
  skipped_no_recipient: ['Skipped', ''],
  skipped_stale: ['Skipped', ''],
  dry_run: ['Dry run', 'amber'],
  deferred: ['Waiting', 'amber'],
  sending: ['Sending', 'amber'],
  failed: ['Failed', 'red'],
};
function StatusChip({ status }) {
  const [label, tone] = STATUS_CHIPS[status] || [status, ''];
  return <span className={`adm-chip ${tone ? `adm-chip--${tone}` : ''}`}>{label}</span>;
}

/** Push rows store the user id as the recipient, or '*' when there was no device. */
function Recipient({ m }) {
  if (m.channel !== 'push') return m.recipient;
  if (m.recipient === '*') return <span className="act-nodevice">No device — app not installed</span>;
  return 'App on their phone';
}

const STEPS = [
  { field: 'fre_completed', label: 'Finished first-run setup', hint: 'completed the onboarding screens' },
  { field: 'succeeded_7d', label: 'Activated within 7 days', hint: 'added profile data beyond first-run' },
  { field: 'returned_day2', label: 'Came back by day 2', hint: 'signed in on local day 1 or 2' },
  { field: 'returned_day7', label: 'Came back around day 7', hint: 'signed in on local day 6–8' },
];

const ZERO = { enrolled: 0, fre_completed: 0, succeeded_7d: 0, returned_day2: 0, returned_day7: 0 };

function BarCell({ n, enrolled, holdout }) {
  const p = share(n, enrolled);
  return (
    <div className="act-barcell">
      <span className="act-barcell__val">{n} · {pctText(p)}</span>
      <span className="act-bar">
        <span className={`act-bar__fill ${holdout ? 'act-bar__fill--pale' : ''}`} style={{ width: `${p || 0}%` }} />
      </span>
    </div>
  );
}

function Overview({ rem, hold }) {
  if (rem.enrolled + hold.enrolled === 0) {
    return <p className="adm-empty" data-testid="activation-empty">Nobody enrolled in this window.</p>;
  }
  const remRate = share(rem.succeeded_7d, rem.enrolled);
  const holdRate = share(hold.succeeded_7d, hold.enrolled);
  const lift = remRate !== null && holdRate !== null ? remRate - holdRate : null;
  const thin = rem.enrolled < 10 || hold.enrolled < 10;
  let liftSub = null;
  if (hold.enrolled === 0) liftSub = 'Needs a holdout group to compare against';
  else if (thin) liftSub = `Only ${hold.enrolled} in the holdout — not yet a reliable read`;

  return (
    <>
      <div className="act-kpis">
        <div className="act-kpi act-kpi--hero" data-testid="activation-group-reminders-new">
          <div className="act-kpi__label">Got reminders · activated in 7 days</div>
          <div className="act-kpi__value">{pctText(remRate)}</div>
          <div className="act-kpi__sub">
            <strong>{rem.succeeded_7d} of {rem.enrolled}</strong> added profile data
          </div>
        </div>
        <div className="act-kpi" data-testid="activation-group-holdout-new">
          <div className="act-kpi__label">Holdout (no reminders) · activated in 7 days</div>
          <div className="act-kpi__value">{pctText(holdRate)}</div>
          <div className="act-kpi__sub">
            {hold.enrolled
              ? `${hold.succeeded_7d} of ${hold.enrolled} added profile data`
              : 'Nobody landed in the holdout this window'}
          </div>
        </div>
        <div className="act-kpi" data-testid="activation-lift">
          <div className="act-kpi__top">
            <span className="act-kpi__label">Lift from reminders</span>
            {thin && <span className="adm-chip adm-chip--amber">Thin data</span>}
          </div>
          <div className={`act-kpi__value ${lift !== null && lift < 0 ? 'act-kpi__value--neg' : 'act-kpi__value--pos'}`}>
            {lift === null ? '—' : `${lift >= 0 ? '+' : '−'}${Math.abs(lift)} pts`}
          </div>
          {liftSub && <div className="act-kpi__sub">{liftSub}</div>}
        </div>
      </div>

      <div className="act-compare">
        <div className="act-compare__head">
          <h3>New signups</h3>
          <span>Enrolled at signup</span>
        </div>
        <div className="adm-tablewrap">
          <table className="act-table act-table--compare" data-testid="activation-compare">
            <thead>
              <tr>
                <th style={{ width: '34%' }}>Step</th>
                <th className="act-th--green">Got reminders · {rem.enrolled} enrolled</th>
                <th>Holdout · {hold.enrolled} enrolled</th>
              </tr>
            </thead>
            <tbody>
              {STEPS.map((s) => (
                <tr key={s.field}>
                  <td>
                    <div className="act-step">{s.label}</div>
                    <div className="act-hint">{s.hint}</div>
                  </td>
                  <td><BarCell n={rem[s.field]} enrolled={rem.enrolled} /></td>
                  <td><BarCell n={hold[s.field]} enrolled={hold.enrolled} holdout /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Messages({ data }) {
  const [logOpen, setLogOpen] = useState(false);
  const counts = data.status_counts || {};
  const c = (k) => counts[k] || 0;
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const skipped = c('skipped_no_recipient') + c('skipped_stale');
  const legend = [
    { key: 'sent', label: 'Sent', n: c('sent'), tone: 'sent' },
    { key: 'skipped', label: 'Skipped', reason: 'no device to push to', n: c('skipped_no_recipient'), tone: 'skipped' },
    ...(counts.skipped_stale ? [{ key: 'stale', label: 'Skipped', reason: 'too late', n: c('skipped_stale'), tone: 'skipped' }] : []),
    { key: 'failed', label: 'Failed', n: c('failed'), tone: 'failed' },
    { key: 'deferred', label: 'Waiting', reason: 'held by the daily cap', n: c('deferred'), tone: 'waiting' },
    ...(counts.dry_run ? [{ key: 'dry', label: 'Dry run', reason: 'not sent', n: c('dry_run'), tone: 'waiting' }] : []),
    ...(counts.sending ? [{ key: 'sending', label: 'Sending', reason: 'outcome unknown', n: c('sending'), tone: 'waiting' }] : []),
  ];
  const channels = data.channel_counts || {};
  const channelRow = (ch) => {
    const cc = channels[ch] || {};
    const t = Object.values(cc).reduce((a, b) => a + b, 0);
    return { sent: cc.sent || 0, total: t };
  };
  const recent = data.recent || [];

  return (
    <>
      <div className="act-msgs">
        <div className="act-panel" data-testid="activation-status-counts">
          <div className="act-panel__label">Reminder rows this window</div>
          <div className="act-panel__headline">
            <span className="act-panel__num">{total}</span>
            <span>{c('sent')} delivered · {skipped + c('failed')} couldn&apos;t be sent</span>
          </div>
          <div className="act-stack">
            {total > 0 && legend.filter((l) => l.n).map((l) => (
              <span key={l.key} className={`act-tone--${l.tone}`} style={{ width: `${(l.n / total) * 100}%` }} />
            ))}
          </div>
          <ul className="act-legend">
            {legend.map((l) => (
              <li key={l.key}>
                <span className={`act-legend__swatch act-tone--${l.tone}`} />
                <span className="act-legend__label">
                  {l.label}{l.reason && <span className="act-legend__reason"> — {l.reason}</span>}
                </span>
                <span className="act-legend__n">{l.n}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="act-panel" data-testid="activation-channels">
          <div className="act-panel__label">By channel</div>
          {[
            { ch: 'email', name: 'Email', note: 'Welcome email goes to the signup address.' },
            { ch: 'push', name: 'Push', note: `${data.no_device_athletes || 0} athlete${data.no_device_athletes === 1 ? ' has' : 's have'} not installed the app yet, so there is no device to send to.` },
          ].map(({ ch, name, note }) => {
            const r = channelRow(ch);
            return (
              <div className="act-channel" key={ch}>
                <div className="act-channel__row">
                  <span className="act-channel__name">{name}</span>
                  <span>{r.sent} of {r.total} sent</span>
                </div>
                <span className="act-bar act-bar--full">
                  <span className="act-bar__fill" style={{ width: `${share(r.sent, r.total) || 0}%` }} />
                </span>
                <div className="act-hint act-channel__note">{note}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="act-fold">
        <button type="button" className="act-fold__btn" aria-expanded={logOpen}
          onClick={() => setLogOpen((o) => !o)} data-testid="activation-log-toggle">
          <span>
            <span className="act-fold__title">Message log</span>
            <span className="act-fold__meta">Newest 200 rows, any window · dry-run rows show what would have been sent</span>
          </span>
          <span className="act-fold__action">
            {logOpen ? 'Hide' : `Show ${recent.length} rows`} <span className="act-caret">▼</span>
          </span>
        </button>
        {logOpen && (
          <div className="adm-tablewrap act-fold__body">
            <table className="act-table act-table--boxed" data-testid="activation-recent">
              <thead>
                <tr><th>When</th><th>Athlete</th><th>Message</th><th>Asked them to</th><th>Sent to</th><th>Result</th></tr>
              </thead>
              <tbody>
                {recent.map((m) => (
                  <tr key={m.id}>
                    <td className="act-td--soft act-td--nowrap">{fmtWhen(m.at)}</td>
                    <td className="act-td--name">{m.athlete}</td>
                    <td className="act-td--nowrap">
                      <span className={`act-dot act-dot--${m.channel}`} />{stepLabel(m.step_key)}
                    </td>
                    <td>{askLabel(m.ask)}</td>
                    <td><Recipient m={m} /></td>
                    <td><StatusChip status={m.status} /></td>
                  </tr>
                ))}
                {recent.length === 0 && <tr><td colSpan={6}>No messages yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function Stalled({ rows }) {
  return (
    <>
      <p className="act-intro">Still getting reminders, nothing added yet. Newest first, up to 200.</p>
      <div className="act-boxwrap adm-tablewrap">
        <table className="act-table act-table--boxed" data-testid="activation-stalled">
          <thead>
            <tr>
              <th>Athlete</th><th>Signed up</th><th>Track</th><th>Next reminder asks for</th>
              <th className="act-num">Reminders sent</th><th>Last one</th><th>Opened a link</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.athlete_user_id}>
                <td className="act-td--name">{s.name}</td>
                <td className="act-td--soft act-td--nowrap">{fmtDay(s.day0_at)}</td>
                <td><span className="adm-chip">{s.track === 'catchup' ? 'Catch-up' : 'New'}</span></td>
                <td>{s.next_ask ? askLabel(s.next_ask) : 'Nothing — sequence done'}</td>
                <td className="act-num">{s.messages_sent}</td>
                <td className="act-td--soft act-td--nowrap">{fmtDay(s.last_sent_at)}</td>
                <td>
                  {s.clicked > 0
                    ? <span className="act-yes">Yes · {s.clicked}</span>
                    : <span className="act-muted">Not yet</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7}>No stalled athletes.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

const VIEWS = ['overview', 'messages', 'stalled'];

/**
 * Signup activation reminders: did reminders get new athletes to add profile
 * data, against the 15% holdout that gets none? Overview / Messages / Stalled
 * sub-tabs (design handoff "Scorecard" 1a, 2026-10-29). Only the `new` track is
 * shown; the catch-up track was a one-time backfill.
 */
export default function ActivationReport() {
  const [days, setDays] = useState(7);
  const [view, setView] = useState('overview');
  const { data, loading, error } = useReport('activation', 'all', `&days=${days}`);

  const groups = (data?.groups || []).filter((g) => g.track === 'new');
  const rem = groups.find((g) => g.group === 'reminders') || ZERO;
  const hold = groups.find((g) => g.group === 'holdout') || ZERO;
  const enrolled = rem.enrolled + hold.enrolled;
  const messageTotal = Object.values(data?.status_counts || {}).reduce((a, b) => a + b, 0);
  const stalled = data?.stalled || [];

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'messages', label: 'Messages', count: messageTotal },
    { key: 'stalled', label: 'Stalled athletes', count: stalled.length },
  ];
  const onTabKey = (e) => {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
    if (!step) return;
    const next = VIEWS[(VIEWS.indexOf(view) + step + VIEWS.length) % VIEWS.length];
    setView(next);
    e.currentTarget.parentElement.querySelector(`[data-view="${next}"]`)?.focus();
  };

  return (
    <section className="report-section act">
      <header className="act-header">
        <div>
          <h2>Activation</h2>
          <p className="act-header__sub">
            Do reminders get new athletes to add profile data? Reminders vs. a 15% holdout that gets none.
          </p>
        </div>
        <div className="act-header__window">
          <span>Enrolled in the last</span>
          <AdminWindowSwitcher options={WINDOWS} value={days} onChange={setDays} label="Enrolled in the last" />
        </div>
      </header>

      {loading && <p className="report-loading">Loading…</p>}
      {error && <p className="report-error">{error}</p>}

      {!loading && !error && data && (
        <>
          <div className="act-subbar">
            <div className="act-tabs" role="tablist" aria-label="Activation views">
              {tabs.map((t) => (
                <button key={t.key} type="button" role="tab" data-view={t.key}
                  aria-selected={view === t.key} tabIndex={view === t.key ? 0 : -1}
                  className={`act-tab ${view === t.key ? 'act-tab--active' : ''}`}
                  onClick={() => setView(t.key)} onKeyDown={onTabKey}>
                  {t.label}
                  {t.count !== undefined && <span className="act-tab__count">{t.count}</span>}
                </button>
              ))}
            </div>
            <span className="act-measured">
              Measured across <strong>{enrolled} enrolled athlete{enrolled === 1 ? '' : 's'}</strong>
            </span>
          </div>

          {view === 'overview' && (
            <>
              <Overview rem={rem} hold={hold} />
              <details className="act-defs">
                <summary>How these are counted <span className="act-caret">▼</span></summary>
                <p>
                  &quot;Activated&quot; is the first profile write within 7 days of day 0. &quot;Came back&quot; is a
                  sign-in on a later local date than day 0 in the athlete&apos;s time zone (by state; Chicago when
                  unknown): day 2 is any sign-in on local days 1–2, day 7 any on local days 6–8.
                </p>
              </details>
            </>
          )}
          {view === 'messages' && <Messages data={data} />}
          {view === 'stalled' && <Stalled rows={stalled} />}
        </>
      )}
    </section>
  );
}
